import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Coins, Download, Filter, MonitorSmartphone, Share2, Trophy, Users } from 'lucide-react'
import {
  Alert,
  Breadcrumbs,
  Button,
  ConfirmDialog,
  Menu,
  PageContainer,
  PageHeader,
  Pagination,
  SearchField,
  SegmentedControl,
  Select,
  StatePanel,
} from '@/components/common'
import { TableSurface } from '@/components/common/data/TableSurface'
import { DashboardFilterControls } from '@/components/growth/DashboardFilterControls'
import { DashboardSection } from '@/components/growth/DashboardSection'
import { formatDateRangeLabel, localDateKey } from '@/components/growth/dashboardFilters'
import { ToolbarMenu } from '@/components/growth/ToolbarMenu'
import { useOrganization } from '@/contexts/OrganizationContext'
import { PRODUCT_KEYS } from '@/config/studioProducts'
import { affiliateLinksApi } from '@/services/api'
import { CHANNEL_FILTERS, DEVICE_FILTERS, formatHandle, moneyCurrencies, SOURCE_FILTERS, STATUS_FILTERS } from './affiliateLinksFormat'
import { AffiliatesTable, LinksTable, SharersTable } from './LiveLinksTables'
import {
  ConversionWidget,
  KeyMetrics,
  LinksBySportWidget,
  OpensByChannelWidget,
  OpensByDeviceWidget,
  PlacementWidget,
  SendsByChannelWidget,
  TopAffiliatesWidget,
  TopSharersWidget,
  TrendWidget,
} from './LiveLinksWidgets'
import { useLiveLinksFilters } from './useLiveLinksFilters'
import styles from './LiveLinksPage.module.css'

const PAGE_SIZE = 25
const VIEW_LABELS = { links: 'Links', sharers: 'Sharers', affiliates: 'Affiliates' }
const DEFAULT_SORT = { by: 'bets', dir: 'desc' }
const SEARCH_PLACEHOLDERS = {
  links: 'Search code, player ID or @handle',
  sharers: 'Search player ID',
  affiliates: 'Search @handle',
}
const VIEW_NOTES = {
  sharers: 'Sharers are the players behind player links. Marketing links are not included here.',
  affiliates: 'Affiliates and their marketing links. Active affiliates are listed even with no activity.',
}

function errorMessage(error) {
  const data = error?.response?.data
  return (data && typeof data === 'object' && data.error) || error?.message || 'Something went wrong.'
}

// A failed blob download carries its JSON error body as a Blob.
async function downloadErrorMessage(error) {
  const data = error?.response?.data
  if (data instanceof Blob) {
    try {
      const body = JSON.parse(await data.text())
      if (body?.error) return body.error
    } catch {
      // Not JSON; fall through to the status message.
    }
  }
  return errorMessage(error)
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

// "Oct 1 – Oct 7, 2026" for the export's tooltip and the file name's dates.
function rangeDescription(range) {
  if (!range) return ''
  const last = new Date(new Date(range.end_at).getTime() - 1)
  return formatDateRangeLabel(localDateKey(new Date(range.start_at)), localDateKey(last))
}

// Whether a list query key (['affiliate-links', slug, view, tableParams, …])
// reads the same organization, grouping and filters.
function sameList(queryKey, [slug, view, tableParams]) {
  return Boolean(queryKey) && queryKey[1] === slug && queryKey[2] === view
    && JSON.stringify(queryKey[3]) === JSON.stringify(tableParams)
}

function listRequest(view, slug, params, signal) {
  if (view === 'sharers') {
    // Sharers are always player links; the API ignores a source here.
    const { source: _source, affiliate_id: _affiliate, ...sharerParams } = params
    return affiliateLinksApi.listSharers(slug, sharerParams, { signal })
  }
  if (view === 'affiliates') {
    const { source: _source, affiliate_id: _affiliate, ...affiliateParams } = params
    return affiliateLinksApi.listAffiliateStats(slug, affiliateParams, { signal })
  }
  return affiliateLinksApi.listLinks(slug, params, { signal })
}

function LiveLinksPage() {
  const { currentOrg, hasProductAccess } = useOrganization()
  const slug = currentOrg?.slug
  const canView = hasProductAccess(PRODUCT_KEYS.AFFILIATE_LINKS, 'view')
  const canExport = hasProductAccess(PRODUCT_KEYS.AFFILIATE_LINKS, 'analyze')
  const canPublish = hasProductAccess(PRODUCT_KEYS.AFFILIATE_LINKS, 'publish')
  const queryClient = useQueryClient()
  const {
    filters,
    changeFilters,
    setParams,
    range,
    pageParams,
    previousParams,
    source,
    affiliate,
    views,
    view,
    status,
    search,
    device,
    channel,
    sport,
    currencyParam,
  } = useLiveLinksFilters()

  const [searchInput, setSearchInput] = useState(search)
  const [sorts, setSorts] = useState({})
  const sort = sorts[view] || DEFAULT_SORT
  const [copyStatus, setCopyStatus] = useState('')
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')
  const [endTarget, setEndTarget] = useState(null)
  const [endError, setEndError] = useState('')

  const summary = useQuery({
    queryKey: ['affiliate-links', slug, 'summary', pageParams],
    queryFn: ({ signal }) => affiliateLinksApi.getSummary(slug, pageParams, { signal }),
    enabled: Boolean(slug && pageParams) && canView,
  })
  const previousSummary = useQuery({
    queryKey: ['affiliate-links', slug, 'summary', previousParams],
    queryFn: ({ signal }) => affiliateLinksApi.getSummary(slug, previousParams, { signal }),
    enabled: Boolean(slug && previousParams) && canView,
  })
  const affiliatesQuery = useQuery({
    queryKey: ['affiliate-links', slug, 'affiliates'],
    queryFn: ({ signal }) => affiliateLinksApi.listAffiliates(slug, { signal }),
    enabled: Boolean(slug) && canView && source !== 'user',
    staleTime: 60_000,
  })
  const affiliates = affiliatesQuery.data?.items || []

  // Money is shown one currency at a time: the URL's, else the one with the
  // most stake in the period.
  const currencies = useMemo(() => moneyCurrencies(summary.data?.totals), [summary.data])
  const currency = currencies.includes(currencyParam) ? currencyParam : (currencies[0] || '')

  const tableParams = useMemo(() => (pageParams ? {
    ...pageParams,
    ...(status ? { status } : {}),
    ...(search ? { search } : {}),
  } : null), [pageParams, status, search])
  // Any change to what is listed starts again on page 1.
  const listKey = JSON.stringify([view, tableParams, sort, currency])
  const [pageState, setPageState] = useState({ key: listKey, page: 1 })
  const page = pageState.key === listKey ? pageState.page : 1
  const listQuery = useQuery({
    queryKey: ['affiliate-links', slug, view, tableParams, sort, sort.by === 'stake' || sort.by === 'ggr' ? currency : '', page],
    queryFn: ({ signal }) => listRequest(view, slug, {
      ...tableParams,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      sort_by: sort.by,
      sort_dir: sort.dir,
      ...((sort.by === 'stake' || sort.by === 'ggr') && currency ? { currency } : {}),
    }, signal),
    enabled: Boolean(slug && tableParams) && canView,
    // Keep the rows while paging or sorting the same list. Another
    // organization, grouping or filter is a different list: its old rows
    // would not match (and could still offer End link), so it loads afresh.
    placeholderData: (previous, previousQuery) => (sameList(previousQuery?.queryKey, [slug, view, tableParams]) ? keepPreviousData(previous) : undefined),
  })

  const endMutation = useMutation({
    mutationFn: (link) => affiliateLinksApi.endLink(slug, link.id),
    onSuccess: (_updated, link) => {
      setEndTarget(null)
      setCopyStatus(`Ended link ${link.code}`)
      queryClient.invalidateQueries({ queryKey: ['affiliate-links', slug] })
    },
    onError: (error) => {
      setEndTarget(null)
      setEndError(`The link could not be ended: ${errorMessage(error)}`)
    },
  })

  const onSort = (column) => setSorts((current) => {
    const active = current[view] || DEFAULT_SORT
    const dir = active.by === column ? (active.dir === 'asc' ? 'desc' : 'asc') : 'desc'
    return { ...current, [view]: { by: column, dir } }
  })
  const changeView = (next) => {
    // A search is for one kind of row (a code is not a handle), so it goes.
    setSearchInput('')
    setParams({ view: next === 'links' ? '' : next, q: '' })
  }
  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link.url)
      setCopyStatus(`Copied link ${link.code}`)
    } catch {
      setCopyStatus(`Could not copy link ${link.code}. Copy it from ${link.url}`)
    }
  }
  const exportCSV = async (kind) => {
    setExporting(true)
    setExportError('')
    try {
      const first = localDateKey(new Date(range.start_at))
      const last = localDateKey(new Date(new Date(range.end_at).getTime() - 1))
      let blob
      let filename
      if (kind === 'links') {
        const linkSort = sorts.links || DEFAULT_SORT
        blob = await affiliateLinksApi.downloadLinks(slug, {
          ...tableParams,
          sort_by: linkSort.by,
          sort_dir: linkSort.dir,
          ...((linkSort.by === 'stake' || linkSort.by === 'ggr') && currency ? { currency } : {}),
        })
        filename = `affiliate-links-${slug}-${first}-to-${last}.csv`
      } else if (kind === 'sharers') {
        const sharerSort = sorts.sharers || DEFAULT_SORT
        blob = await affiliateLinksApi.downloadSharers(slug, {
          ...tableParams,
          sort_by: sharerSort.by,
          sort_dir: sharerSort.dir,
          ...((sharerSort.by === 'stake' || sharerSort.by === 'ggr') && currency ? { currency } : {}),
        })
        filename = `affiliate-link-sharers-${slug}-${first}-to-${last}.csv`
      } else {
        // The payout export remains period-scoped: it is the reconciliation
        // file for every credited bet placed in the period.
        blob = await affiliateLinksApi.downloadCreditedBets(slug, { ...range })
        filename = `credited-bets-${slug}-${first}-to-${last}.csv`
      }
      downloadBlob(blob, filename)
    } catch (error) {
      setExportError(`Export failed: ${await downloadErrorMessage(error)}`)
    } finally {
      setExporting(false)
    }
  }

  // Sharers are players, so a marketing or affiliate filter leaves none.
  const showSharers = source !== 'marketing' && !affiliate
  const showAffiliates = source !== 'user' && !affiliate
  const showPlacement = source !== 'marketing' && !affiliate

  const header = (
    <PageHeader
      minimal
      title="Live links"
      subtitle="Marketing links and links players share, and the bets they bring in."
      breadcrumbs={(
        <Breadcrumbs
          items={[
            { label: 'Affiliate links', href: '/dashboard/affiliate-links/live' },
            { label: 'Live links' },
          ]}
          renderLink={(item) => <Link to={item.href}>{item.label}</Link>}
        />
      )}
      actions={canView && canExport ? (
        <Menu
          align="end"
          triggerLabel="Export"
          trigger={(
            <Button
              variant="secondary"
              loading={exporting}
              disabled={!slug || !range || !tableParams}
              title={range ? `Export rows for ${rangeDescription(range)}` : 'Choose both dates of the custom range first'}
            >
              <Download size={16} aria-hidden="true" />
              Export
            </Button>
          )}
          items={[
            { key: 'credited', label: 'Credited bets', icon: Download, onSelect: () => exportCSV('credited') },
            { key: 'links', label: 'Links', icon: Download, onSelect: () => exportCSV('links') },
            ...(showSharers ? [{ key: 'sharers', label: 'Sharers', icon: Download, onSelect: () => exportCSV('sharers') }] : []),
          ]}
        />
      ) : null}
    />
  )

  if (!canView) {
    return (
      <PageContainer size="wide">
        {header}
        <StatePanel state="restricted" title="Affiliate links is not enabled" description="This organization does not have access to Affiliate links." />
      </PageContainer>
    )
  }

  const totals = summary.data?.totals
  const nothingShared = Boolean(totals) && !totals.links && !totals.opens && !totals.bets && !source && !affiliate
  const affiliateItems = [
    { value: '', label: 'All affiliates' },
    ...affiliates.map((item) => ({ value: item.id, label: `${formatHandle(item.handle)} · ${item.name}${item.active ? '' : ' (inactive)'}` })),
  ]
  const sportItems = [
    { value: '', label: 'All sports' },
    ...[...new Set([...(summary.data?.sports || []), sport].filter(Boolean))].sort((a, b) => a.localeCompare(b)).map((item) => ({ value: item, label: item })),
  ]
  const items = listQuery.data?.items || []
  const total = listQuery.data?.total || 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  let listState = null
  if (listQuery.isLoading) listState = <StatePanel state="loading" title={`Loading ${VIEW_LABELS[view].toLowerCase()}`} />
  else if (listQuery.isError) listState = <StatePanel state="error" title={`${VIEW_LABELS[view]} could not load`} description={errorMessage(listQuery.error)} onRetry={() => listQuery.refetch()} />
  else if (!items.length && nothingShared && view === 'links' && !status && !search) {
    listState = (
      <StatePanel
        state="empty"
        title="No links in this period"
        description="Links appear here once players share bets or marketing links are created, and people open them."
        action={canPublish ? <Link className={styles.stateLink} to="/dashboard/affiliate-links/create">Create a marketing link</Link> : null}
      />
    )
  } else if (!items.length) listState = <StatePanel state="empty" title={`No ${VIEW_LABELS[view].toLowerCase()} in this period`} description="Try a longer period or clear the filters." />

  return (
    <PageContainer size="wide">
      {header}
      <div className={styles.page}>
        <div className={styles.toolbar} role="group" aria-label="Live links filters">
          <DashboardFilterControls filters={filters} onChange={changeFilters} />
          <ToolbarMenu
            label="Source"
            icon={Filter}
            value={source}
            items={SOURCE_FILTERS}
            onChange={(value) => setParams({ source: value, ...(value === 'user' ? { affiliate: '' } : {}) })}
          />
          {source !== 'user' && affiliates.length ? (
            <ToolbarMenu label="Affiliate" icon={Users} value={affiliate} items={affiliateItems} onChange={(value) => setParams({ affiliate: value })} />
          ) : null}
          <ToolbarMenu label="Device" icon={MonitorSmartphone} value={device} items={DEVICE_FILTERS} onChange={(value) => setParams({ device: value })} />
          <ToolbarMenu label="Channel" icon={Share2} value={channel} items={CHANNEL_FILTERS} onChange={(value) => setParams({ channel: value })} />
          {sportItems.length > 1 ? (
            <ToolbarMenu label="Sport" icon={Trophy} value={sport} items={sportItems} onChange={(value) => setParams({ sport: value })} />
          ) : null}
          {currencies.length > 1 ? (
            <ToolbarMenu
              label="Currency"
              icon={Coins}
              value={currency}
              items={currencies.map((code) => ({ value: code, label: code }))}
              onChange={(value) => setParams({ currency: value === currencies[0] ? '' : value })}
            />
          ) : null}
        </div>

        {exportError ? <Alert variant="error">{exportError}</Alert> : null}
        {endError ? <Alert variant="error">{endError}</Alert> : null}
        <p className={styles.liveRegion} role="status" aria-live="polite">{copyStatus}</p>

        {!range ? (
          <StatePanel state="empty" title="Choose a date range" description="Pick both the first and the last day of the custom range." />
        ) : summary.isError ? (
          <StatePanel state="error" title="Link totals could not load" description={errorMessage(summary.error)} onRetry={() => summary.refetch()} />
        ) : (
          <>
            {previousParams && previousSummary.isError ? (
              <Alert variant="warning">
                <span className={styles.alertLine}>
                  The previous period could not load, so changes from it are not shown.
                  <Button variant="secondary" size="compact" onClick={() => previousSummary.refetch()}>Retry</Button>
                </span>
              </Alert>
            ) : null}
            <DashboardSection title="Key metrics" subtitle="Counted bets and stake in the selected period.">
              <KeyMetrics summary={summary} previous={previousParams ? previousSummary : null} compare={filters.compare} currency={currency} currencies={currencies} />
            </DashboardSection>

            <DashboardSection title="Performance" subtitle="How links convert, over time and by where they come from.">
              <div className={styles.charts}>
                <ConversionWidget summary={summary} previous={previousParams ? previousSummary : null} compare={filters.compare} />
                <TrendWidget slug={slug} range={range} pageParams={pageParams} previousParams={previousParams} compare={filters.compare} currency={currency} />
              </div>
              {showAffiliates || showSharers || showPlacement ? (
                <div className={styles.lists}>
                  {showAffiliates ? <TopAffiliatesWidget slug={slug} params={pageParams} onSelect={(id) => setParams({ affiliate: id, view: '' })} /> : null}
                  {showSharers ? (
                    <TopSharersWidget
                      slug={slug}
                      params={pageParams}
                      onSelect={(playerId) => { setSearchInput(playerId); setParams({ q: playerId, view: '' }) }}
                    />
                  ) : null}
                  {showPlacement ? <PlacementWidget summary={summary} /> : null}
                </div>
              ) : null}
              <div className={styles.lists}>
                <SendsByChannelWidget summary={summary} />
                <OpensByChannelWidget summary={summary} />
                <OpensByDeviceWidget summary={summary} />
                <LinksBySportWidget summary={summary} />
              </div>
            </DashboardSection>

            <DashboardSection
              title={VIEW_LABELS[view]}
              subtitle={VIEW_NOTES[view]}
              actions={views.length > 1 ? (
                <SegmentedControl
                  label="Group by"
                  items={views.map((value) => ({ value, label: VIEW_LABELS[value] }))}
                  value={view}
                  onValueChange={changeView}
                />
              ) : null}
            >
              <div className={styles.tableToolbar}>
                <SearchField
                  className={styles.search}
                  value={searchInput}
                  onChange={(value) => {
                    setSearchInput(value)
                    if (!value) setParams({ q: '' })
                  }}
                  onSubmit={() => setParams({ q: searchInput.trim() })}
                  placeholder={SEARCH_PLACEHOLDERS[view]}
                  label={SEARCH_PLACEHOLDERS[view]}
                />
                <Select className={styles.statusSelect} aria-label="Status" value={status} onChange={(event) => setParams({ status: event.target.value })}>
                  {STATUS_FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Select>
              </div>
              <TableSurface
                label={VIEW_LABELS[view]}
                busy={listQuery.isFetching}
                state={listState}
                footer={!listState && pageCount > 1 ? (
                  <Pagination
                    page={page}
                    pageCount={pageCount}
                    onPageChange={(next) => setPageState({ key: listKey, page: next })}
                  />
                ) : null}
              >
                {view === 'sharers' ? <SharersTable items={items} sort={sort} onSort={onSort} currency={currency} /> : null}
                {view === 'affiliates' ? (
                  <AffiliatesTable items={items} sort={sort} onSort={onSort} currency={currency} onSelect={(id) => setParams({ affiliate: id, view: '' })} />
                ) : null}
                {view === 'links' ? (
                  <LinksTable items={items} sort={sort} onSort={onSort} currency={currency} onCopy={copyLink} canEnd={canPublish} onEnd={(link) => { setEndError(''); setEndTarget(link) }} />
                ) : null}
              </TableSurface>
            </DashboardSection>
          </>
        )}
      </div>
      <ConfirmDialog
        open={Boolean(endTarget)}
        title="End this link?"
        message={endTarget ? `People who open ${endTarget.code} after this see that the bet is no longer available. Bets already credited to it stay credited.` : ''}
        confirmLabel="End link"
        isDestructive
        loading={endMutation.isPending}
        onConfirm={() => endMutation.mutate(endTarget)}
        onCancel={() => setEndTarget(null)}
      />
    </PageContainer>
  )
}

export default LiveLinksPage
